import React, { useCallback, useEffect, useState } from "react";
import {
  clone,
  identity,
  ifElse,
  mergeRight,
  pipe,
  pluck,
  startsWith,
  tail,
} from "ramda";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import { lensProp, set } from "ramda";
import Grid from "@material-ui/core/Grid";
import { FetchError } from "../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { notBlank } from "../util/ext";
import { useRipEndpointCreate } from "./ripEndpointServices";
import { Box } from "@material-ui/core";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const cleanPath = pipe(ifElse(startsWith("/"), tail, identity));

const useStyles = makeStyles(theme => ({
  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const RipEndpointDialog = props => {
  const { endpoint, modules, open, onClose, onSave } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [fields, setFields] = useState({});
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (endpoint && endpoint.ripEndpointId) {
      setFields(clone(endpoint));
    } else {
      setFields({});
    }
  }, [endpoint]);

  const [selectedModules, setSelectedModules] = useState([]);
  const handleModulesChange = useCallback((_event, modules) => {
    setSelectedModules(modules);
  }, []);

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const endpointCreate = useRipEndpointCreate();
  const createRipEndpoint = async ep => {
    console.log("Creating Endpoint");
    if (
      notBlank(ep.nuviqueOrganizationId) &&
      notBlank(ep.ripEndpointName) &&
      notBlank(ep.ripEndpoint)
    ) {
      await endpointCreate.mutateAsync({ endpoint: ep });
    } else {
      throw new Error("All values are required.");
    }
  };

  const handleError = (message, err) => {
    console.log("Handling Error...");
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else if (err.status === 404) {
        showAlert("Invalid Nuvique Organization Id");
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedEndpoint = mergeRight(endpoint, {
      nuviqueOrganizationId: fields.nuviqueOrganizationId,
      ripEndpointName: fields.ripEndpointName,
      ripEndpoint: cleanPath(fields.ripEndpoint),
      defaultModules: pluck("id", selectedModules),
    });

    try {
      await createRipEndpoint(mergedEndpoint);
      onSave(mergedEndpoint);
    } catch (err) {
      console.log(err);
      handleError("Unable to save SFTP Account.", err);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        <Box display="flex" justifyContent="space-between">
          <Box>New RIP Endpoint</Box>
          <Box>
            {endpointCreate.isLoading && (
              <Box flexGrow="1" display="flex" justifyContent="flex-start">
                <Box>
                  <PanelLoadingIndicator size={20} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>Enter RIP Endpoint Details</DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="organizationId"
              label="Nuvique Organization Id"
              fullWidth
              value={fields.nuviqueOrganizationId || ""}
              onChange={onFieldChanged("nuviqueOrganizationId")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="name"
              label="Endpoint Name"
              fullWidth
              value={fields.ripEndpointName || ""}
              onChange={onFieldChanged("ripEndpointName")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="path"
              label="Upload Path"
              fullWidth
              value={fields.ripEndpoint || ""}
              onChange={onFieldChanged("ripEndpoint")}
            />
          </Grid>

          <Grid item sm={12}>
            {notBlank(modules) ? (
              <Box mt={4} className={classes.multiPicker}>
                <Autocomplete
                  multiple
                  id="modules"
                  options={modules}
                  getOptionLabel={option => option.name}
                  value={selectedModules || []}
                  filterSelectedOptions
                  onChange={handleModulesChange}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Default Modules"
                      placeholder="Select..."
                    />
                  )}
                />
              </Box>
            ) : (
              <Skeleton variant="text" />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleOnClose}
          disabled={endpointCreate.isLoading}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleOnSave}
          disabled={endpointCreate.isLoading}
          color="primary"
        >
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default RipEndpointDialog;
